import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "motion/react";

import { ProfileForm } from "./ProfileForm.tsx";
import WelcomeStep from "./WelcomeStep";

import useUserStore from "@/features/auth/store/useUserStore.ts";

interface ProfileStepProps {
  onNext: () => void;
}

const ProfileStep: React.FC<ProfileStepProps> = ({ onNext }) => {
  const { user } = useUserStore();
  const [currentStep, setCurrentStep] = useState<"welcome" | "profile">("welcome");

  // Check if user has any profile data to determine if we should show welcome step
  useEffect(() => {
    if (user?.specialRoleChecks?.has_public_profile) {
      // User already has a profile, skip welcome step
      setCurrentStep("profile");
    } else {
      // User has no profile, start with welcome step
      setCurrentStep("welcome");
    }
  }, [user]);

  const handleWelcomeNext = () => {
    setCurrentStep("profile");
  };

  const handleProfileComplete = () => {
    onNext();
  };

  return (
    <motion.div
      animate={{ opacity: 1, y: 0 }}
      className={"h-full w-full"}
      initial={{ opacity: 0, y: 20 }}
      transition={{ duration: 0.5 }}
    >
      <AnimatePresence mode="wait">
        {currentStep === "welcome" ? (
          <motion.div
            key="welcome"
            animate={{ opacity: 1, x: 0 }}
            className="flex h-full w-full items-center"
            exit={{ opacity: 0, x: -50 }}
            initial={{ opacity: 0, x: 50 }}
            transition={{ duration: 0.3 }}
          >
            <WelcomeStep onNext={handleWelcomeNext} />
          </motion.div>
        ) : (
          <motion.div
            key="profile"
            animate={{ opacity: 1, x: 0 }}
            className="h-full w-full"
            exit={{ opacity: 0, x: 50 }}
            initial={{ opacity: 0, x: -50 }}
            transition={{ duration: 0.3 }}
          >
            <ProfileForm onComplete={handleProfileComplete} />
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

export default ProfileStep;
